import { Desc } from '@sm/intl';

import COPY from '~pricing/copy';

export const ROOT_PRICING_ROUTE = '/plans-and-pricing';

export enum Sources {
  PRICINGWEB = 'pricingweb',
}

export enum Packages {
  Basic = 'BASIC',

  StandardMonthly = 'STANDARD_MONTHLY',
  StandardAnnual = 'STANDARD_ANNUAL', // This is preserve because of canonical name (which it may never change)

  StarterMonthly = 'STARTER_MONTHLY',
  StarterAnnual = 'STANDARD_ANNUAL', // Please note Starter Annual is STANDARD_ANNUAL and is preserved due to canonical reasons.

  AdvantageMonthly = 'ADVANTAGE_MONTHLY',
  AdvantageAnnual = 'ADVANTAGE_ANNUAL',

  PremierMonthly = 'PREMIER_MONTHLY',
  PremierAnnual = 'PREMIER_ANNUAL',

  TeamAdvantage = 'TEAM_ADVANTAGE_ANNUAL',
  TeamPremier = 'TEAM_PREMIER_ANNUAL',

  Enterprise = 'ENT_PLATINUM',
  Flex = 'ANALYZE_MONTHLY',

  FormsMonthly = 'FORMS_MONTHLY',
  FormsAnnual = 'FORMS_ANNUAL',
}

export const PackageIds: Record<Packages, number> = {
  [Packages.Basic]: 1,
  [Packages.StandardMonthly]: 31,
  [Packages.StandardAnnual]: 32, // See Packages

  [Packages.StarterMonthly]: 31,
  [Packages.StarterAnnual]: 32,

  [Packages.AdvantageMonthly]: 33,
  [Packages.AdvantageAnnual]: 34,

  [Packages.PremierMonthly]: 35,
  [Packages.PremierAnnual]: 36,

  [Packages.TeamAdvantage]: 134,
  [Packages.TeamPremier]: 136,

  [Packages.Enterprise]: 26,
  [Packages.Flex]: 37,

  [Packages.FormsMonthly]: 41,
  [Packages.FormsAnnual]: 42,
};

export const ComparePlanIds: string[] = [
  Packages.Basic,
  Packages.StandardMonthly,
  Packages.StarterAnnual,
  Packages.AdvantageAnnual,
  Packages.PremierAnnual,
  Packages.TeamAdvantage,
  Packages.TeamPremier,
  Packages.Enterprise,
];

export enum LanguageCodes {
  PT = 'pt',
  EN = 'en-US',
  GB = 'en-GB',
  FR = 'fr',
  ES = 'es',
}

export enum SkuTypes {
  CoreSeat = 'SUBSCRIPTION_CORE_SEAT',
  AdditionalSeat = 'SUBSCRIPTION_ADDITIONAL_SEAT',
  ResponseOverage = 'RESPONSES',
}

/**
 * Localization should only happen in the L10nProvider
 * See https://github.com/pages/mntv-webplatform/smweb/#/pages/webs/localization?id=ltl10nprovidergt-t-and-lttgt
 */
export const PackageDisplayNames: Record<Packages, Desc> = {
  [Packages.Basic]: COPY.BASIC,
  [Packages.StandardMonthly]: COPY.STANDARD_MONTHLY,
  [Packages.StandardAnnual]: COPY.STANDARD_ANNUAL, // In the process of decommissioning but who knows, maybe never
  [Packages.StarterMonthly]: COPY.STARTER_MONTHLY,
  [Packages.StarterAnnual]: COPY.STARTER_ANNUAL,
  [Packages.AdvantageMonthly]: COPY.ADVANTAGE_MONTHLY,
  [Packages.AdvantageAnnual]: COPY.ADVANTAGE_ANNUAL,
  [Packages.PremierMonthly]: COPY.PREMIER_MONTHLY,
  [Packages.PremierAnnual]: COPY.PREMIER_ANNUAL,
  [Packages.TeamAdvantage]: COPY.TEAM_ADVANTAGE,
  [Packages.TeamPremier]: COPY.TEAM_PREMIER,
  [Packages.Enterprise]: COPY.ENT_PLATINUM,
  [Packages.Flex]: COPY.STANDARD_MONTHLY,
  [Packages.FormsMonthly]: COPY.FORMS,
  [Packages.FormsAnnual]: COPY.FORMS,
};

export const MarketedFeatures: string[] = [
  'survey_create_limit',
  'create_question_limit',
  'base_response_count',
  'num_free_responses',
  'email_support',
  'priority_email_support',
  'phone_support',
  'quizzes_pro',
  'analyze_export_enabled',
  'analyze_export_spss_enabled',
  'download_as_csv_xls_pdf',
  'download_as_ppt',
  'create_skip_logic_enabled',
  'create_piping_enabled',
  'funneling_enabled',
  'statistical_significance',
  'show_sig_diffs_enabled',
  'create_logo_enabled',
  'create_custom_theme_enabled',
  'collector_friendly_url_enabled',
  'create_quotas_enabled',
  'a_b_testing',
  'create_randomization_enabled',
  'create_custom_variables_enabled',
  'payment_qt',
  'file_upload',
  'plans_benchmarks',
  'analyze_access_benchmarking',
  'benchmarks',
  'click_map_qt',
  'collaboration',
  'build_surveys_together',
  'share_with_your_organization',
  'analyze_can_export_hide_branding',
  'analyze_combine_filters',
  'analyze_sentiment_enabled',
  'sa_en_ga',
  'analyze_trends_enabled',
  'asset_library',
  'group_templates',
  'lite_library',
  'shared_library',
  'shared_group_library',
  'team_themes',
  'custom_templates',
  'collector_completion_url_enabled',
  'collector_popup_enabled',
  'collector_white_label_enabled',
  'create_toggle_footer_enabled',
  'disable_footer_branding_enabled',
  'hide_export_branding_enabled',
  'hipaa_enabled',
  'multilingual',
  'multiple_block_rotation_enabled',
  'private_apps',
  'response_quality_enabled',
  'show_create_crosstab',
  'filter_crosstab',
  'team_ownership',
  'user_management',
  'add_users',
  'advanced_logic',
  'admin_dashboard',
  'single_sign_on',
  'analyze_results_together',
  'analyze_integrations',
  'analyze_can_share_customize_branding',
  'extended_piping',
  'essential_question_types',
  'collector_create_limit',
  'pop_up_collector',
  'track_email_responses',
  'recurring_surveys',
  'kiosk_mode_inactivity_timer',
  'kiosk_mode_passcode_lock',
  'consolidated_billing',
  'all_languages_supported',
  'pagination',
  'labeling_titles_numbering',
  'create_templates_enabled',
  'question_library',
  '508_accessibility',
  'matrix_question_type',
  'collect_contact_information',
  'video_question_type',
  'rating_question_type',
  'menu_matrix_enabled',
  'progress_bar',
  'text_formatting',
  'premium_themes',
  'collector_thank_you_enabled',
  'customize_disqualification_page_enabled',
  'carry_forward',
  'real_time_results',
  'share_surveys',
  'analyze_can_customize_charts',
  'create_print_enabled',
  'analyze_ta_enabled',
  'analyze_can_ta_tag',
  'require_answer',
  'validate_answer',
  'randomize_answer_choices',
  'set_survey_end_date',
  'view_respondent_ip_address',
  'record_respondent_email_address',
  'set_max_response_count',
  'respondent_receipt',
  'password_protection',
  'enable_ip_blocking',
  'send_surveys',
  'collector_mobile_sdk_enabled',
  'mobile_sdk_pull_response_data_enabled',
  'ranking_question_type',
  'slider_question_type',
  'create_custom_html_email_message_enabled',
  'create_grayscale_footer',
  'analyze_dashboard_password_enabled',
  'extract_data',
  'best_worst_qt',
];
